import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useMediaQuery } from './hooks/useMediaQuery'
import { OverlayScrollbars } from 'overlayscrollbars'
import { killBodyScroll, initBodyScroll } from "@assets/scripts/common/overlayScrollbars.js"
import ResourcesOptions from './Options'

const ResourcesFilters = ({
  terms,
  selectedFilters,
  validOptions,
  onFilterChange,
  onClearFilter,
  onClearAll,
  hasActiveFilters,
  filtersLabel,
  searchPlaceholder,
  isLoading,
  searchValue,
  onSearchChange,
  onManualSearch,
  isSearchPending,
  hasTerms
}) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [isOpen, setIsOpen] = useState(false)

  // Local state for mobile filter selections (before applying)
  const [localMobileFilters, setLocalMobileFilters] = useState(() => {
    // Initialize with current selected filters
    return selectedFilters || {}
  })

  // Sync local mobile filters with parent selectedFilters when they change
  useEffect(() => {
    if (!isMobile) {
      // Desktop doesn't use local filters, always use parent state
      return
    }

    // Only update local state if not currently open (to avoid overriding user's uncommitted changes)
    if (!isOpen) {
      setLocalMobileFilters(selectedFilters || {})
    }
  }, [selectedFilters, isMobile, isOpen])

  const [openDropdowns, setOpenDropdowns] = useState(() => {
    // Only open the first fieldset by default on mobile
    if (isMobile && terms.length > 0) {
      return new Set([terms[0].name])
    }
    return new Set()
  })
  const filtersOptionsRef = useRef(null)
  const overlayScrollbarsRef = useRef(null)

  const handleFilterToggle = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  const handleDropdownToggle = useCallback((termName) => {
    setOpenDropdowns(prev => {
      const newSet = new Set(prev)
      if (newSet.has(termName)) {
        // Close this dropdown
        newSet.delete(termName)
      } else {
        // Open this dropdown
        if (!isMobile) {
          // On desktop, close all other dropdowns first (accordion behavior)
          newSet.clear()
        }
        newSet.add(termName)
      }
      return newSet
    })
  }, [isMobile])

  const closeAllDropdowns = useCallback(() => {
    // Only close all dropdowns on desktop, not on mobile
    if (!isMobile) {
      setOpenDropdowns(new Set())
    } else {
      // On mobile, keep the first fieldset open
      if (terms.length > 0) {
        setOpenDropdowns(new Set([terms[0].name]))
      }
    }
  }, [isMobile, terms])

  const handleClearGroup = useCallback((filterName) => {
    // On desktop (1024px+), fire the filter immediately
    if (!isMobile) {
      onFilterChange(filterName, [])
    }
  }, [isMobile, onFilterChange])

  const handleFilterChange = useCallback((filterName, values) => {
    if (isMobile) {
      // On mobile, store the changes locally without triggering the filter
      // The actual filtering will happen when Apply is clicked
      setLocalMobileFilters(prev => {
        const newFilters = { ...prev }

        if (values && Array.isArray(values) && values.length > 0) {
          // Remove duplicates and empty values
          const cleanValues = [...new Set(values.filter(Boolean))]
          newFilters[filterName] = cleanValues
        } else {
          // Remove the filter if it has no values
          delete newFilters[filterName]
        }

        return newFilters
      })
    } else {
      // On desktop (1024px+), filter immediately
      onFilterChange(filterName, values)
      // Don't close dropdowns on desktop - let user control them
    }
  }, [isMobile, onFilterChange])

  const handleClearFilter = useCallback((filterName) => {
    onClearFilter(filterName)
    // Only close dropdowns on desktop, not on mobile
    if (!isMobile) {
      closeAllDropdowns()
    }
  }, [onClearFilter, isMobile])

  const handleClearAll = useCallback(() => {
    onClearAll()
    // Only close dropdowns on desktop, not on mobile
    if (!isMobile) {
      closeAllDropdowns()
    }
  }, [onClearAll, isMobile])

  const activeFiltersCount = useMemo(() => {
    if (!selectedFilters || typeof selectedFilters !== 'object') return 0
    return Object.values(selectedFilters).reduce((total, values) => total + (Array.isArray(values) ? values.length : 0), 0)
  }, [selectedFilters])

  // Initialize OverlayScrollbars when mobile filters open
  useEffect(() => {
    if (isOpen && isMobile && filtersOptionsRef.current) {
      try {
        // Kill body scroll first
        killBodyScroll()

        // Initialize OverlayScrollbars for the filters options
        overlayScrollbarsRef.current = OverlayScrollbars(filtersOptionsRef.current, {
          scrollbars: {
            autoHide: 'never',
          },
          overflow: {
            x: 'hidden',
            y: 'scroll',
          },
        })

        // Cleanup function
        return () => {
          if (overlayScrollbarsRef.current) {
            try {
              overlayScrollbarsRef.current.destroy()
            } catch (error) {
              console.warn('Error destroying OverlayScrollbars:', error)
            }
            overlayScrollbarsRef.current = null
          }
          // Re-init body scroll
          initBodyScroll()
        }
      } catch (error) {
        console.error('Error initializing OverlayScrollbars:', error)
        // Ensure body scroll is restored on error
        initBodyScroll()
      }
    }
  }, [isOpen, isMobile])

  // Cleanup OverlayScrollbars when component unmounts
  useEffect(() => {
    return () => {
      if (overlayScrollbarsRef.current) {
        try {
          overlayScrollbarsRef.current.destroy()
        } catch (error) {
          console.warn('Error destroying OverlayScrollbars on unmount:', error)
        }
        overlayScrollbarsRef.current = null
      }
      // Ensure body scroll is restored
      initBodyScroll()
    }
  }, [])

  const handleSearchSubmit = useCallback((e) => {
    if (e) {
      e.preventDefault()
    }

    // Trigger manual search
    if (onManualSearch) {
      onManualSearch()
    }

    if (isMobile) {
      // On mobile, apply all the locally stored filters at once when search is submitted
      Object.entries(selectedFilters).forEach(([filterName, values]) => {
        if (values && values.length > 0) {
          onFilterChange(filterName, values)
        }
      })
      // Close the mobile filters popup after search
      setIsOpen(false)
    }
    // Only close dropdowns on desktop, not on mobile
    if (!isMobile) {
      closeAllDropdowns()
    }
  }, [isMobile, selectedFilters, onFilterChange, onManualSearch])

  const handleApplyFilters = useCallback(() => {
    if (isMobile) {
      // On mobile, apply all the locally stored filters at once
      Object.entries(localMobileFilters).forEach(([filterName, values]) => {
        if (values && values.length > 0) {
          onFilterChange(filterName, values)
        }
      })

      // Also clear any filters that were removed locally
      Object.keys(selectedFilters || {}).forEach(filterName => {
        if (filterName !== 'query' && (!localMobileFilters[filterName] || localMobileFilters[filterName].length === 0)) {
          onFilterChange(filterName, [])
        }
      })

      // Close the mobile filters popup after applying
      setIsOpen(false)
    }
    // Only close dropdowns on desktop, not on mobile
    if (!isMobile) {
      closeAllDropdowns()
    }
  }, [isMobile, localMobileFilters, selectedFilters, onFilterChange])

  const handleClearAllFilters = useCallback(() => {
    onClearAll()
    // Clear local mobile state as well
    if (isMobile) {
      setLocalMobileFilters({})
    }
    // Only close dropdowns on desktop, not on mobile
    if (!isMobile) {
      closeAllDropdowns()
    }
    // Close the mobile filters popup after clearing
    if (isMobile) {
      setIsOpen(false)
    }
  }, [onClearAll, isMobile])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isOpen) {
          setIsOpen(false)
        }
        closeAllDropdowns()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Only handle click outside on desktop (not mobile)
      if (!isMobile && isOpen && !e.target.closest('.resources__filters')) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isOpen, isMobile])

  useEffect(() => {
    const handleDropdownClickOutside = (e) => {
      // Only handle dropdown click outside on desktop, not on mobile
      if (!isMobile) {
        // Don't close dropdowns if we're clicking on clear buttons or other dropdown content
        const isOutsideDropdown = !e.target.closest('.resources__options--dropdown') &&
                                 !e.target.closest('.resources__options--toggle') &&
                                 !e.target.closest('.resources__options--clear-btn')

        if (isOutsideDropdown && openDropdowns.size > 0) {
          closeAllDropdowns()
        }
      }
    }

    document.addEventListener('click', handleDropdownClickOutside)
    return () => document.removeEventListener('click', handleDropdownClickOutside)
  }, [openDropdowns.size, isMobile])

  // If no terms and no search, don't render anything
  if (!hasTerms && !searchPlaceholder) {
    return null
  }

  return (
    <div className="resources__filters">
      {/* Only show mobile toggle button if we have terms */}
      {isMobile && hasTerms && (
        <button
          className="resources__filters--toggle"
          onClick={handleFilterToggle}
          aria-expanded={isOpen}
        >
          {filtersLabel}
          {activeFiltersCount > 0 && (
            <span className="resources__filters--count">({activeFiltersCount})</span>
          )}
        </button>
      )}

      {/* Always use the wrapper, but control mobile behavior with isOpen state */}
      {hasTerms ? (
        <div className={`resources__filters--wrapper ${isOpen ? 'is-active' : ''}`}>
          {/* Only show mobile close button if we have terms (modal mode) */}
          {isMobile && (
            <button
              className="resources__filters--close"
              onClick={handleFilterToggle}
              aria-label="Close filters"
              type="button"
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M10 10L30 30M30 10L10 30" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          )}

          <div className="resources__filters--label">
            {filtersLabel}
          </div>

          <div
            ref={filtersOptionsRef}
            className="resources__filters--scroll"
          >
            <div className="resources__filters--options">
              {/* Show term filters */}
              {terms.map((term) => {
                const isOpen = openDropdowns.has(term.name)

                // Use local mobile filters on mobile, parent selectedFilters on desktop
                const currentSelectedValues = isMobile
                  ? (localMobileFilters[term.name] || [])
                  : (selectedFilters && selectedFilters[term.name]) || []

                return (
                  <ResourcesOptions
                    key={term.name}
                    term={term}
                    selectedValues={currentSelectedValues}
                    selectedFilters={isMobile ? localMobileFilters : selectedFilters}
                    validOptions={validOptions[term.name] || []}
                    isOpen={isOpen}
                    onToggle={() => handleDropdownToggle(term.name)}
                    onChange={(values) => handleFilterChange(term.name, values)}
                    onClearFilter={() => handleClearFilter(term.name)}
                    onClearGroup={() => handleClearGroup(term.name)}
                    isLoading={isLoading}
                  />
                )
              })}

              {searchPlaceholder && (
                <div className="resources__filters--search">
                  <div className="resources__filters--search-input-wrapper">
                    <input
                      type="text"
                      name="main-search"
                      className="resources__filters--search-input"
                      placeholder={searchPlaceholder}
                      value={searchValue || ''}
                      onChange={(e) => {
                        onSearchChange(e.target.value)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleSearchSubmit(e)
                        }
                      }}
                      disabled={isLoading}
                      data-search-type="main"
                      aria-label="Search resources"
                    />
                    {(searchValue && searchValue.length > 0) && (
                      <button
                        id="clear-main-search"
                        className="resources__filters--search-clear"
                        onClick={() => onSearchChange('')}
                        aria-label="Clear main search"
                        type="button"
                      >
                        &#10006;
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className={`resources__filters--search-submit ${isSearchPending ? 'is-pending' : ''}`}
                    onClick={(e) => handleSearchSubmit(e)}
                    disabled={isLoading || !searchValue || searchValue.length === 0}
                    aria-label="Search resources"
                    title={isSearchPending ? "Search will trigger automatically in a few seconds, or click to search now" : "Search resources"}
                  >
                    <span className="screen-reader-text">
                      {isSearchPending ? "Search pending - will trigger automatically" : "Search"}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Show action buttons for terms */}
          <div className="resources__filters--actions actions">
            <div className="type-outline">
              <button
                type="button"
                className="resources__filters--clear"
                onClick={handleClearAllFilters}
                disabled={isLoading}
              >
                Clear all filters
              </button>
            </div>

            <div>
              <button
                type="button"
                className="resources__filters--apply"
                onClick={handleApplyFilters}
                disabled={isLoading}
              >
                Apply filters
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Search-only case - no wrapper, direct flex layout */
        <div className="resources__filters--search-only">
          <div className="resources__filters--label">
            {filtersLabel}
          </div>
          <div className="resources__filters--search">
            <div className="resources__filters--search-input-wrapper">
              <input
                type="text"
                name="main-search"
                className="resources__filters--search-input"
                placeholder={searchPlaceholder}
                value={searchValue || ''}
                onChange={(e) => {
                  onSearchChange(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSearchSubmit(e)
                  }
                }}
                disabled={isLoading}
                data-search-type="main"
                aria-label="Search resources"
              />
              {(searchValue && searchValue.length > 0) && (
                <button
                  id="clear-main-search"
                  className="resources__filters--search-clear"
                  onClick={() => onSearchChange('')}
                  aria-label="Clear main search"
                  type="button"
                >
                  &#10006;
                </button>
              )}
            </div>
            <button
              type="button"
              className={`resources__filters--search-submit ${isSearchPending ? 'is-pending' : ''}`}
              onClick={(e) => handleSearchSubmit(e)}
              disabled={isLoading || !searchValue || searchValue.length === 0}
              aria-label="Search resources"
              title={isSearchPending ? "Search will trigger automatically in a few seconds, or click to search now" : "Search resources"}
            >
              <span className="screen-reader-text">
                {isSearchPending ? "Search pending - will trigger automatically" : "Search"}
              </span>
            </button>
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <div className="resources__filters--current">
          <button
            className="resources__filters--clear-all"
            onClick={handleClearAllFilters}
          >
            Clear All Filters
          </button>


          {selectedFilters?.query && (
            <button
              className="resources__filters--tag"
              onClick={() => onClearFilter('query')}
            >
              Search: {selectedFilters.query}
            </button>
          )}

          {selectedFilters && Object.entries(selectedFilters).map(([filterName, values]) => {
            if (filterName === 'query' || !values || values.length === 0) return null

            return values.map((value, index) => {
              const term = terms.find(t => t.name === filterName)

              return (
                <button
                  key={`${filterName}-${value}-${index}`}
                  className="resources__filters--tag"
                  onClick={() => {
                    const newValues = values.filter(v => v !== value)
                    onFilterChange(filterName, newValues)
                  }}
                >
                  {term?.terms?.find(t => t.slug === value)?.name || value}
                </button>
              )
            })
          })}

        </div>
      )}
    </div>
  )
}

export default ResourcesFilters
